import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from "@material-tailwind/react";
import { FileUploader } from "react-drag-drop-files";
import { Typography, Select, Option } from "@material-tailwind/react";
import { useNavigate } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import moment from 'moment';
import ApiService from 'utills/ApiService';
import OrganizationWPS from "interfaces";
import toast from 'react-hot-toast';
import { PhotographIcon, XIcon } from '@heroicons/react/outline';
import { PHONE_CODE } from 'utills/constants';
import { FullScreenLoading } from 'components/fullScreenLoading';
import './edit.scss';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const fileTypes = ["JPG", "PNG", "JPEG"];
const fileLicenseType = ["JPG", "PNG", "JPEG", "PDF"];

const FormEditOrganization = (props: any) => {
  const { translate, isMobile, token } = props;

  const [error, setError] = useState({
    cover_banner: '',
    organization_logo: '',
    organization_banner: '',
    brand_logo: '',
    rectangular_promotion_banner: '',
    square_promotion_banner: '',
    seller_company_name: '',
    seller_company_email: ''
  } as any);
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState({} as OrganizationWPS);
  const navigate = useNavigate();
  const editor = useRef(null as any);
  const [companyLicense, setCompanyLicense] = useState([] as any);
  const [phoneCode, setPhoneCode] = useState('');

  const config = useMemo(
    () => ({
      readonly: false,
      style: {
        height: 'auto',
        overflow: 'auto'
      },
      uploader: {
        insertImageAsBase64URI: true
      },
    }),
    []
  );

  useEffect(() => {
    if (Object.keys(organization).length === 0) {
      fetchOrganizationDetail();
    }
  }, [organization]);

  const fetchOrganizationDetail = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`organization`, token);

      if (result) {

        setOrganization(result);

        if (result?.seller_business_license && result?.seller_business_license.length > 0) {
          setCompanyLicense(result?.seller_business_license);
        }

        if (result?.seller_company_phone_area_code && result?.seller_company_phone_area_code !== '') {
          setPhoneCode(result?.seller_company_phone_area_code);
        } else {
          setPhoneCode('84');
        }
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  }

  const handleSelectImage = (file: any, type: string, width?: number, height?: number) => {
    setLoading(true);
    const errs = { ...error };
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = (e: any) => {
      const img = new Image() as any;

      img.src = e.target.result;

      if (width && height) {
        img.onload = async function () {
          const img_height = this.height;
          const img_width = this.width;

          if (width !== img_width || height !== img_height) {
            errs[type] = translate('error-upload-image', {
              size: `${width} x ${height}`,
            });

            setLoading(false);
          } else {
            errs[type] = '';
            handleUploadImage(file, type);
          }

          setError({ ...errs });

        }
      } else {
        errs[type] = '';
        handleUploadImage(file, type);

        setError({ ...errs });
      }

    };

  };

  const handleSelectLicense = async (files: any) => {
    setLoading(true);

    const fileKeys = Object.keys(files);
    const arr = [...companyLicense] as any;

    if (fileKeys.length > 0) {
      const uploadPromises = fileKeys.map((key) => handleUploadLicense(files[key]));

      const results = await Promise.all(uploadPromises);

      const validResults = results.filter((res) => res);

      setCompanyLicense([...arr, ...validResults]);
      setLoading(false);

    } else {
      setLoading(true);
    }

  }

  const handleUploadLicense = async (file: any) => {
    const data = new FormData();

    data.append('upload', file, `${file.name}`);

    try {
      const result = await ApiService.uploadImage(data);

      if (result) {
        return result.img_url;
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  }

  const handleUploadImage = async (img: any, type: string) => {
    const data = new FormData();
    data.append('upload', img, `${img.name}`);

    try {
      const result = await ApiService.uploadImage(data);

      if (result) {
        handleChangeValue(result.img_url, type)
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  }

  const handleUploadImagePost = async (img: any) => {
    let res = '';
    const data = new FormData();
    data.append('upload', img, `${img.name}`);

    try {
      const result = await ApiService.uploadImage(data);

      if (result) {
        res = result.img_url
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    return res;
  }

  const handleRemoveImage = (type: string) => {
    setLoading(true);

    const temp_file = { ...organization } as any;

    temp_file[type] = '';

    setOrganization({ ...temp_file });

    setLoading(false);
  }

  const handleSubmit = async () => {
    setLoading(true);

    const errKeys = Object.keys(error);
    let active = true;
    const errs = _.cloneDeep(error) as any;

    errKeys.forEach((key: string) => {
      if (error[key] !== '') {
        active = false
      }
    });

    if (active) {
      if (!organization?.seller_company_name || organization?.seller_company_name === '') {
        active = false;

        errs['seller_company_name'] = translate('field-required');

        setError({ ...errs })
      }
    }

    if (active) {
      const submitData = { ...organization } as any;

      submitData.seller_business_issue_date = (submitData.seller_business_issue_date && submitData.seller_business_issue_date !== '') ? moment(submitData.seller_business_issue_date).format('YYYY-MM-DD') : submitData.seller_business_issue_date;

      submitData.seller_business_license = companyLicense;
      delete submitData.is_active;
      delete submitData.name;

      try {
        const result = await ApiService.put(`organization`, submitData, token);

        if (result) {
          toast.success(translate("update-successfully"));

          const redirect = setInterval(() => {
            navigate("/organization/info");
            clearInterval(redirect);
          }, 2000);
        }
      } catch (error: any) {
        toast.error(error.message);
      }
    }

    setLoading(false);
  }

  const handleChangeValue = (val: any, type: string, lang?: string) => {
    const values = { ...organization } as any;
    const errs = _.cloneDeep(error);

    if (!lang) {
      const onlyNumbers = val.replace(/\D/g, '');

      if (type === 'seller_company_phone') {
        values[type] = onlyNumbers
      } else {
        values[type] = val;
      }

      if (type === 'seller_company_name' || type === 'seller_business_address' || type === 'seller_company_tax' || type === 'seller_company_phone' || type === 'seller_business_certificate_number') {
        if (!val || val === '') {
          errs[type] = translate('field-required')
        } else {
          errs[type] = ''
        }
      }

      if (type === 'seller_company_email') {
        if (!val || val === '') {
          errs[type] = translate('field-required')
        } else {
          const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,8}$/i;

          if (regex.test(val)) {
            errs[type] = '';
          } else {
            errs[type] = translate('wrong-format-email');
          }
        }
      }

    } else {
      values[type][lang] = val;
    }

    setOrganization({ ...values });
    setError({ ...errs });
  }

  const dataURLtoFile = (dataurl: any, filename: any) => {

    var arr = dataurl.splitranslate(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  const getLicenseName = (url: string) => {
    const position = url.lastIndexOf('/');

    const name = url.slice(position + 1);

    return name
  }

  const handleRemoveLicense = (url: string) => {
    const arr = _.cloneDeep(companyLicense) as any;

    const updatedFiles = arr.filter((item: string) => item !== url);

    setCompanyLicense([...updatedFiles]);
  }

  if (Object.keys(organization).length > 0) {
    return (
      <div className={`${isMobile ? 'w-full' : 'w-[80%]'} grid grid-cols-1 gap-[16px]`} id="form-edit-organization">
        <div className="w-full bg-white rounded-[8px]">
          <div className="w-full h-[56px] flex items-center border-b border-[#d9e1e9] px-[16px] text-14 font-600">{translate('edit')} {translate('images-and-colors')}</div>

          <div className="w-full p-[16px] grid grid-cols-1 gap-[16px]">
            <div className={`w-full flex items-start gap-[16px] ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="w-full">
                <div className="w-full flex items-center mb-[8px]">
                  <span className="text-14 font-500">{translate('cover-banner')} (1400 x 300)</span>

                  <LightTooltip title={translate('tooltip-cover-image')} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                {
                  organization.cover_banner !== '' ?
                    <div className="w-full h-[173px] bg-[#f5f6f7] border border-[#c5ccd2] flex items-end justify-center rounded-[8px]" onClick={() => handleRemoveImage("cover_banner")}>
                      <img
                        src={organization.cover_banner}
                        alt="upload-organization"
                        className="m-auto max-h-[150px] max-w-[80%] object-contain relative"
                      />

                      <img src="/images/icons/icon-remove.png" className="size-[30px] flex items-center justify-center cursor-pointer rounded-full relative right-[45px] bottom-[110px]" alt="icon-remove" />

                    </div>
                    :
                    <FileUploader
                      handleChange={(e: any) => handleSelectImage(e, "cover_banner", 1400, 300)}
                      name="file"
                      types={fileTypes}
                      classes="w-full bg-[#f5f6f7] border border-[#c5ccd2] rounded-[4px] h-[173px] flex flex-col items-center justify-center rounded-[8px]"
                    >
                      <PhotographIcon className="size-[32px] text-[#d9e1e9]" />

                      <Typography
                        {...{} as any} className="text-14 text-[#868e95] mt-[32px] mb-[10px]">
                        {translate("drag-and-drop")}
                      </Typography>

                      <div className="text-14 text-brand-500 font-700 cursor-pointer">{translate("choose-image")}</div>
                    </FileUploader>
                }


                {
                  error.cover_banner !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.cover_banner}</div>
                }

              </div>

              <div className="w-full">
                <div className="w-full flex items-center mb-[8px]">
                  <span className="text-14 font-500">{translate('organization-logo')} (600 x 600)</span>

                  <LightTooltip title={translate('tooltip-organization-logo')} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                {
                  organization.organization_logo !== '' ?
                    <div className="w-full h-[173px] bg-[#f5f6f7] border border-[#c5ccd2] flex items-end justify-center rounded-[8px]" onClick={() => handleRemoveImage("organization_logo")}>
                      <img
                        src={organization.organization_logo}
                        alt="upload-organization"
                        className="m-auto w-[150px] h-[150px] object-contain relative"
                      />

                      <img src="/images/icons/icon-remove.png" className="size-[30px] flex items-center justify-center cursor-pointer rounded-full relative right-[45px] bottom-[110px]" alt="icon-remove" />
                    </div>
                    :
                    <FileUploader
                      handleChange={(e: any) => handleSelectImage(e, "organization_logo", 600, 600)}
                      name="file"
                      types={fileTypes}
                      classes="w-full bg-[#f5f6f7] border border-[#c5ccd2] rounded-[4px] h-[173px] flex flex-col items-center justify-center rounded-[8px]"
                    >
                      <PhotographIcon className="size-[32px] text-[#d9e1e9]" />

                      <Typography
                        {...{} as any} className="text-14 text-[#868e95] mt-[32px] mb-[10px]">
                        {translate("drag-and-drop")}
                      </Typography>

                      <div className="text-14 text-brand-500 font-700 cursor-pointer">{translate('choose-image')}</div>
                    </FileUploader>
                }

                {
                  error.organization_logo !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.organization_logo}</div>
                }

              </div>
            </div>

            <div className={`w-full flex items-center grid gap-[16px] ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="form-row-item">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('rectangular-promotion-banner')} (950 x 300)</span>

                  <LightTooltip title={translate('tooltip-rectangular-promotion-banner')} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                {
                  organization.rectangular_promotion_banner && organization.rectangular_promotion_banner !== '' ?
                    <div className="w-full h-[173px] bg-[#f5f6f7] border border-[#c5ccd2] flex items-end justify-center rounded-[8px]" onClick={() => handleRemoveImage("rectangular_promotion_banner")}>
                      <img
                        src={organization.rectangular_promotion_banner}
                        alt="upload-organization"
                        className="m-auto max-h-[150px] max-w-[80%] object-contain relative"
                      />

                      <img src="/images/icons/icon-remove.png" className="size-[30px] flex items-center justify-center cursor-pointer rounded-full relative right-[45px] bottom-[110px]" alt="icon-remove" />

                    </div>
                    :
                    <FileUploader
                      handleChange={(e: any) => handleSelectImage(e, "rectangular_promotion_banner", 950, 300)}
                      name="file"
                      types={fileTypes}
                      classes="w-full bg-[#f5f6f7] border border-[#c5ccd2] rounded-[4px] h-[173px] flex flex-col items-center justify-center rounded-[8px]"
                    >
                      <PhotographIcon className="size-[32px] text-[#d9e1e9]" />

                      <Typography
                        {...{} as any} className="text-14 text-[#868e95] mt-[32px] mb-[10px]">
                        {translate("drag-and-drop")}
                      </Typography>

                      <div className="text-14 text-brand-500 font-700 cursor-pointer">{translate("choose-image")}</div>
                    </FileUploader>
                }


                {
                  error.rectangular_promotion_banner !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.rectangular_promotion_banner}</div>
                }

              </div>

              <div className="form-row-item">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('square-promotion-banner')} (300 x 300)</span>

                  <LightTooltip title={translate('tooltip-rectangular-promotion-banner')} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                {
                  (organization.square_promotion_banner && organization.square_promotion_banner !== '') ?
                    <div className="w-full h-[173px] bg-[#f5f6f7] border border-[#c5ccd2] flex items-end justify-center rounded-[8px]" onClick={() => handleRemoveImage("square_promotion_banner")}>
                      <img
                        src={organization.square_promotion_banner}
                        alt="upload-organization"
                        className="m-auto w-[150px] h-[150px] object-contain relative"
                      />

                      <img src="/images/icons/icon-remove.png" className="size-[30px] flex items-center justify-center cursor-pointer rounded-full relative right-[45px] bottom-[110px]" alt="icon-remove" />
                    </div>
                    :
                    <FileUploader
                      handleChange={(e: any) => handleSelectImage(e, "square_promotion_banner", 300, 300)}
                      name="file"
                      types={fileTypes}
                      classes="w-full bg-[#f5f6f7] border border-[#c5ccd2] rounded-[4px] h-[173px] flex flex-col items-center justify-center rounded-[8px]"
                    >
                      <PhotographIcon className="size-[32px] text-[#d9e1e9]" />

                      <Typography
                        {...{} as any} className="text-14 text-[#868e95] mt-[32px] mb-[10px]">
                        {translate("drag-and-drop")}
                      </Typography>

                      <div className="text-14 text-brand-500 font-700 cursor-pointer">{translate('choose-image')}</div>
                    </FileUploader>
                }

                {
                  error.square_promotion_banner !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.square_promotion_banner}</div>
                }

              </div>
            </div>

            <div className={`w-full flex items-center grid gap-[16px] ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="form-row-item">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('organization-banner')} (1440x600)</span>

                  <LightTooltip title={translate("tooltip-organization-banner")} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                {
                  organization.organization_banner !== '' ?
                    <div className="w-full h-[173px] bg-[#f5f6f7] border border-[#c5ccd2] flex items-end justify-center rounded-[8px]" onClick={() => handleRemoveImage("organization_banner")}>
                      <img
                        src={organization.organization_banner}
                        alt="upload-organization"
                        className="m-auto max-h-[150px] max-w-[80%] object-contain relative"
                      />

                      <img src="/images/icons/icon-remove.png" className="size-[30px] flex items-center justify-center cursor-pointer rounded-full relative right-[45px] bottom-[110px]" alt="icon-remove" />

                    </div>
                    :
                    <FileUploader
                      handleChange={(e: any) => handleSelectImage(e, "organization_banner", 1440, 600)}
                      name="file"
                      types={fileTypes}
                      classes="w-full bg-[#f5f6f7] border border-[#c5ccd2] rounded-[4px] h-[173px] flex flex-col items-center justify-center rounded-[8px]"
                    >
                      <PhotographIcon className="size-[32px] text-[#d9e1e9]" />

                      <Typography
                        {...{} as any} className="text-14 text-[#868e95] mt-[32px] mb-[10px]">
                        {translate('drag-and-drop')}
                      </Typography>

                      <div className="text-14 text-brand-500 font-700 cursor-pointer">{translate('choose-image')}</div>
                    </FileUploader>
                }

                {
                  error.organization_banner !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.organization_banner}</div>
                }

              </div>

              
            </div>

          </div>
        </div>

        <div className="w-full bg-white rounded-[8px]">
          <div className="w-full h-[56px] flex items-center border-b border-[#d9e1e9] px-[16px] text-14 font-600">{translate('edit')} {translate('information')}</div>

          <div className="w-full p-[16px] grid grid-cols-1 gap-[16px]">
            <div className="form-item">
              <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                <span className="text-14 font-600">{translate('organization-name')}</span>

                <LightTooltip title={translate("tooltip-organization-name")} className="w-[200px]" placement='top-start'>
                  <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                </LightTooltip>
              </div>

              <div className="w-full mb-[4px]">
                <div className="w-[164px] h-[40px] bg-[#e9ecff] rounded-t-[4px] flex items-center px-[16px] text-14 font-500">Vietnam</div>

                <div className="w-full h-[42px] rounded-[4px] bg-[#f5f7f6] border border-[#c5ccd2] text-[14px] text-[#868e95] flex items-center px-[16px]">{organization.name.vi}</div>
              </div>

              <div className="w-full mb-[4px]">
                <div className="w-[164px] h-[40px] bg-[#e9ecff] rounded-t-[4px] flex items-center px-[16px] text-14 font-500">English</div>

                <div className="w-full h-[42px] rounded-[4px] bg-[#f5f7f6] border border-[#c5ccd2] text-[14px] text-[#868e95] flex items-center px-[16px]">{organization.name.en}</div>
              </div>
            </div>

            <div className="form-item">
              <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                <span className="text-14 font-600">{translate('country')}</span>

                <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
              </div>

              <div className="select-country">
                <Select
                  onChange={(val) => handleChangeValue(val, 'country')}
                  value={organization.country}
                  {...{} as any}
                >
                  {
                    PHONE_CODE.map((item: any, index: number) => {
                      return (
                        <Option key={`${item.value}_${index}`} value={item.label}>{item.label}</Option>
                      )
                    })
                  }
                </Select>
              </div>

            </div>

            <div className="form-item">
              <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                <span className="text-14 font-600">{translate('organization-introduction')}</span>

                <LightTooltip title={translate("tooltip-organization-introduction")} className="w-[200px]" placement='top-start'>
                  <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                </LightTooltip>
              </div>

              <div className="w-full mb-[16px]">
                <div className="w-[164px] h-[40px] bg-[#e9ecff] rounded-t-[4px] flex items-center px-[16px] text-14 font-500">Vietnam</div>

                <JoditEditor
                  ref={editor}
                  value={organization.introduction.vi}
                  config={config}
                  onBlur={newContent => { }}
                  onChange={async newContent => {
                    const position = newContent.indexOf('src="data:image');
                    let dataTemp = newContent;

                    if (position !== -1) {
                      dataTemp = dataTemp.slice(position + 5);

                      const positionBase64 = dataTemp.indexOf('width');

                      const base64 = dataTemp.slice(0, positionBase64 - 2);

                      const image = await dataURLtoFile(base64, `content_actiup`);

                      let imgLink = await handleUploadImagePost(image);

                      const replaceData = newContent.replace(base64, imgLink);

                      handleChangeValue(replaceData, "introduction", "vi")
                    } else {
                      handleChangeValue(newContent, "introduction", "vi")
                    }
                  }}
                />
              </div>

              <div className="w-full">
                <div className="w-[164px] h-[40px] bg-[#e9ecff] rounded-t-[4px] flex items-center px-[16px] text-14 font-500">English</div>

                <JoditEditor
                  ref={editor}
                  value={organization.introduction.en}
                  config={config}
                  onBlur={newContent => { }}
                  onChange={async newContent => {
                    const position = newContent.indexOf('src="data:image');
                    let dataTemp = newContent;

                    if (position !== -1) {
                      dataTemp = dataTemp.slice(position + 5);

                      const positionBase64 = dataTemp.indexOf('width');

                      const base64 = dataTemp.slice(0, positionBase64 - 2);

                      const image = await dataURLtoFile(base64, `content_actiup`);

                      let imgLink = await handleUploadImagePost(image);

                      const replaceData = newContent.replace(base64, imgLink);

                      handleChangeValue(replaceData, "introduction", "en")
                    } else {
                      handleChangeValue(newContent, "introduction", "en")
                    }
                  }}
                />
              </div>
            </div>
          </div>

        </div>

        <div className="w-full bg-white rounded-[8px]">
          <div className="w-full h-[56px] flex items-center border-b border-[#d9e1e9] px-[16px] text-14 font-600">
            <span className='mr-[10px]'>{translate('seller-identification')}</span>

            <LightTooltip title={translate('tooltip-seller-identification')} className="w-[200px]" placement='top-start'>
              <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
            </LightTooltip>
          </div>

          <div className="w-full p-[16px] grid grid-cols-1 gap-[16px]">
            <div className="form-item">
              <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                <span className="text-14 font-600">{translate('company-license')}</span>
              </div>

              <div className='flex gap-x-[32px] flex-wrap gap-y-[12px]'>
                <FileUploader
                  handleChange={(e: any) => handleSelectLicense(e)}
                  name="file"
                  types={fileLicenseType}
                  multiple={true}
                  classes="bg-[#f5f6f7] border border-[#c5ccd2] w-[240px] h-[64px] rounded-[4px] flex flex-col items-center justify-center"
                >
                  <Typography
                    {...{} as any} className="text-14 text-[#868e95]">
                    {translate("drag-and-drop")}
                  </Typography>

                  <div className="text-14 text-brand-500 font-700 cursor-pointer">{translate("choose-image")}</div>
                </FileUploader>

                {
                  isMobile &&
                  <div className='w-full flex gap-x-[12px] mt-[12px] text-14 text-[#868E95]'>
                    <span>{translate('maximum-file-size')}</span>

                    <img src='/images/icons/icon-point.svg' alt='icon-point' />

                    <span>{translate('support-file-format')}</span>
                  </div>
                }

                {
                  companyLicense?.length > 0 &&
                  <div className='grid grid-cols-1 gap-y-[8px]'>
                    {
                      companyLicense?.map((item: string, index: number) => {
                        return (
                          <div className='h-[28px] px-[12px] rounded-[100px] bg-[#F3E8FF] flex items-center justify-center gap-x-[8px]' key={`company_license_${index}`}>
                            <span className='text-14 text-[#011BB6]'>{getLicenseName(item)}</span>

                            <XIcon className='size-[12px] text-[#011BB6] cursor-pointer' onClick={() => handleRemoveLicense(item)} />
                          </div>
                        )
                      })
                    }
                  </div>
                }
              </div>

              {
                !isMobile &&
                <div className='w-full flex gap-x-[12px] mt-[12px] text-14 text-[#868E95]'>
                  <span>{translate('maximum-file-size')}</span>

                  <img src='/images/icons/icon-point.svg' alt='icon-point' />

                  <span>{translate('support-file-format')}</span>
                </div>
              }

            </div>

            <div className={`w-full flex items-center grid gap-[16px] ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="w-full">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('company-name')}</span>

                  <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                  <LightTooltip title={translate('tooltip-company-name')} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                <input placeholder={translate('company-name')} className='w-full h-[40px] rounded-[8px]' onChange={(e: any) => handleChangeValue(e.target.value, 'seller_company_name')} value={organization?.seller_company_name} />

                {
                  error.seller_company_name !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.seller_company_name}</div>
                }

              </div>

              <div className="w-full">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('company-tax-id')}</span>
                  <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                  <LightTooltip title={translate('tooltip-company-tax-id')} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                <input placeholder={translate('company-tax-id')} className='w-full h-[40px] rounded-[8px]' onChange={(e: any) => handleChangeValue(e.target.value, 'seller_company_tax')} value={organization?.seller_company_tax} />
                
                {
                  error.seller_company_tax !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.seller_company_tax}</div>
                }
              </div>
            </div>

            <div className={`w-full flex items-center grid gap-[16px] ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="w-full">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('company-certificate')}</span>
                  <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                </div>

                <input placeholder={translate('company-certificate')} className='w-full h-[40px] rounded-[8px]' onChange={(e: any) => handleChangeValue(e.target.value, 'seller_business_certificate_number')} value={organization?.seller_business_certificate_number} />
                {
                  error.seller_business_certificate_number !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.seller_business_certificate_number}</div>
                }
              </div>

              <div className="w-full">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('company-issue-date')}</span>
                </div>

                <input placeholder='DD/MM/YYYY' type='date' className='w-full h-[40px] rounded-[8px]' onChange={(e: any) => handleChangeValue(e.target.value, 'seller_business_issue_date')} value={organization?.seller_business_issue_date} />

              </div>
            </div>

            <div className={`w-full flex items-center grid gap-[16px] ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="w-full">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('company-place-of-issue')}</span>
                </div>

                <input placeholder={translate('company-place-of-issue')} className='w-full h-[40px] rounded-[8px]' onChange={(e: any) => handleChangeValue(e.target.value, 'seller_place_of_issue')} value={organization?.seller_place_of_issue} />

              </div>

              <div className="w-full">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('company-business-address')}</span>
                  <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                  <LightTooltip title={translate('tooltip-company-business-address')} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                <input placeholder={translate('company-business-address')} className='w-full h-[40px] rounded-[8px]' onChange={(e: any) => handleChangeValue(e.target.value, 'seller_business_address')} value={organization?.seller_business_address} />
                {
                  error.seller_business_address !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.seller_business_address}</div>
                }
              </div>
            </div>

            <div className={`w-full flex items-center grid gap-[16px] ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="w-full">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">Email</span>
                  <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                  <LightTooltip title={translate('tooltip-company-email')} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                <input placeholder='Email' className='w-full h-[40px] rounded-[8px]' onChange={(e: any) => handleChangeValue(e.target.value, 'seller_company_email')} value={organization?.seller_company_email} />

                {
                  error.seller_company_email !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.seller_company_email}</div>
                }

              </div>

              <div className="w-full">
                <div className="w-full flex items-center gap-x-[4px] mb-[8px]">
                  <span className="text-14 font-600">{translate('phone-number')}</span>
                  <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                  <LightTooltip title={translate('tooltip-phone-number')} className="w-[200px]" placement='top-start'>
                    <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                  </LightTooltip>
                </div>

                <div className="phone-group">
                  {
                    phoneCode !== '' &&
                    <div className="select-phone-code">
                      <Select
                        value={phoneCode}
                        onChange={(val: any) => handleChangeValue(val, 'seller_company_phone_area_code')}
                        {...{} as any}
                      >
                        {
                          PHONE_CODE.map((item: any, index: number) => {
                            return (
                              <Option key={`${item.value}_${index}`}
                                value={item.code}>{item.value} | +{item.code}</Option>
                            )
                          })
                        }
                      </Select>
                    </div>
                  }

                  <input placeholder={translate('phone-number')} className='w-full h-[40px] border-none outline-none focus:outline-none focus:border-none' onChange={(e: any) => handleChangeValue(e.target.value, 'seller_company_phone')} value={organization?.seller_company_phone} />
                </div>
                {
                  error.seller_company_phone !== '' &&
                  <div className="text-12 mt-[4px] font-600 text-red-500">{error.seller_company_phone}</div>
                }
              </div>
            </div>
          </div>
        </div>

        <div className="w-full h-[80px] flex items-center justify-end gap-[12px] fixed bottom-0 right-0 bg-white px-[24px]">
          <Button
            {...{} as any} className="px-[16px] h-[48px] border border-brand-500 bg-white !text-brand-500 flex items-center justify-center text-16" onClick={() => navigate("/organization/info")}>{translate('btn-cancel')}</Button>
          <Button
            {...{} as any}
            type="submit" className="px-[16px] h-[48px] border border-brand-500 bg-brand-500 !text-white flex items-center justify-center text-16" onClick={handleSubmit}>{translate('btn-save')}</Button>
        </div>

        {
          loading &&
          <FullScreenLoading />
        }
      </div>
    )
  } else {
    return (
      <div className="w-full form-group"></div>
    )
  }

}

export default FormEditOrganization;